import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../css/UnitList.css";
import {
  MdEdit,
  MdDelete,
  MdAttachMoney,
  MdHistory,
  MdBarChart,
} from "react-icons/md";

const SupplierList = () => {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL || "";

  const [suppliers, setSuppliers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState(null);

  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const response = await fetch(`${API_URL}supplier-list.php`);
        const data = await response.json();
        if (Array.isArray(data.suppliers)) {
          setSuppliers(data.suppliers);
        } else {
          showToast("Failed to load suppliers", "error");
        }
      } catch (error) {
        showToast("Network error. Please try again.", "error");
      }
    };
    fetchSuppliers();
  }, [API_URL]);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  const filteredSuppliers = suppliers.filter((supplier) => {
    const matchesSearchTerm =
      supplier.suppliername.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.phone.includes(searchTerm) ||
      supplier.email.toLowerCase().includes(searchTerm.toLowerCase());

    // Assuming your supplier object has a 'created_at' field.
    // Adjust 'supplier.created_at' if your date field is named differently.
    const supplierDate = new Date(supplier.created_at);
    const isAfterStartDate = !startDate || supplierDate >= new Date(startDate);
    const isBeforeEndDate = !endDate || supplierDate <= new Date(endDate);

    return matchesSearchTerm && isAfterStartDate && isBeforeEndDate;
  });

  const handleAddSupplier = () => {
    navigate("/add-supplier");
  };

  const handleDeleteClick = (supplierId) => {
    const supplier = suppliers.find((s) => s.id === supplierId);
    setSupplierToDelete(supplier);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!supplierToDelete) return;
    try {
      const formData = new FormData();
      formData.append("id", supplierToDelete.id);
      const response = await fetch(`${API_URL}delete-supplier.php`, {
        method: "POST",
        body: formData,
      });
      const data = await response.json();
      if (
        data.status === "success" ||
        data.message === "Supplier deleted successfully."
      ) {
        showToast("Supplier deleted successfully!", "success");
        setSuppliers((prevSuppliers) =>
          prevSuppliers.filter((s) => s.id !== supplierToDelete.id)
        );
      } else {
        showToast(data.message || "Failed to delete supplier.", "error");
      }
    } catch (error) {
      showToast("Network error. Please try again.", "error");
    }
    setShowDeleteModal(false);
    setSupplierToDelete(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setSupplierToDelete(null);
  };

  return (
    <div className="category-list-content">
      <div className="page-header">
        <div className="header-content">
          <h1>Supplier List</h1>
          <p>Manage all your suppliers</p>
        </div>
        <button className="add-unit-button" onClick={handleAddSupplier}>
          + Add New Supplier
        </button>
      </div>

      <div
        className="filters-section"
        style={{
          display: "flex",
          gap: "20px",
          flexWrap: "wrap",
          marginBottom: "20px",
        }}
      >
        <div className="search-box" style={{ flexGrow: 1 }}>
          <input
            type="text"
            placeholder="Search by name, phone, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="start-date">Start Date</label>
          <input
            type="date"
            id="start-date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{
              padding: "10px",
              borderRadius: "5px",
              border: "1px solid #ccc",
            }}
          />
        </div>
        <div className="form-group">
          <label htmlFor="end-date">End Date</label>
          <input
            type="date"
            id="end-date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={{
              padding: "10px",
              borderRadius: "5px",
              border: "1px solid #ccc",
            }}
          />
        </div>
      </div>

      <div className="unit-list-table-container">
        <table className="unit-list-table">
          <thead>
            <tr>
              <th>Supplier Name</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Address</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSuppliers.length > 0 ? (
              filteredSuppliers.map((supplier) => (
                <tr key={supplier.id}>
                  <td>{supplier.suppliername}</td>
                  <td>{supplier.phone}</td>
                  <td>{supplier.email}</td>
                  <td>{supplier.address}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="action-btn edit"
                        title="Edit"
                        onClick={() =>
                          navigate(`/edit-supplier/${supplier.id}`)
                        }
                      >
                        <MdEdit />
                      </button>
                      <button
                        className="action-btn delete"
                        title="Delete"
                        onClick={() => handleDeleteClick(supplier.id)}
                      >
                        <MdDelete />
                      </button>
                      <button
                        className="action-btn payment"
                        title="Payment"
                        onClick={() =>
                          navigate(`/supplier-payment/${supplier.id}`, {
                            state: { supplierName: supplier.suppliername },
                          })
                        }
                      >
                        <MdAttachMoney />
                      </button>
                      <button
                        className="action-btn payment-history"
                        title="Payment History"
                        onClick={() =>
                          navigate(`/supplier-payment-history/${supplier.id}`, {
                            state: { supplierName: supplier.suppliername },
                          })
                        }
                      >
                        <MdHistory />
                      </button>
                      <button
                        className="action-btn report"
                        title="Report"
                        onClick={() =>
                          navigate(`/supplier-report/${supplier.id}`)
                        }
                      >
                        <MdBarChart />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5">
                  No suppliers found for the selected filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button
              className="toast-close"
              onClick={() => setToast({ show: false, message: "", type: "" })}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {showDeleteModal && supplierToDelete && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Confirm Delete</h3>
            <p>
              Are you sure you want to delete{" "}
              <b>{supplierToDelete.suppliername}</b>?
            </p>
            <div className="modal-actions">
              <button className="modal-btn yes" onClick={handleConfirmDelete}>
                Yes
              </button>
              <button className="modal-btn no" onClick={handleCancelDelete}>
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierList;
